import { SxProps, Theme } from '@mui/material';

import { findingsChipBackground, findingsChipHeightPx } from './FindingsConstants';

const popupIndicatorButtonSx = {
  color: (theme: Theme) => theme.palette.action.active,
  padding: 0,
  marginRight: 0,
  minWidth: '24px',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
    width: '24px',
    height: '24px',
    color: (theme: Theme) => theme.palette.action.active,
  },
} as const;

const endAdornmentSx = {
  right: '13px',
} as const;

/**
 * MUI Autocomplete `Paper` (listbox) and aligned panels (Menu, Radix dropdown).
 * Keeps responsable / ubicación / activos / requisitos dropdowns visually consistent when open.
 */
export const findingTypeAutocompletePaperSx: SxProps<Theme> = {
  marginTop: '4px',
  borderRadius: '8px',
  border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  backgroundColor: (theme: Theme) => theme.palette.common.white,
};

/**
 * Use on `KeyboardArrowDownRounded` in custom triggers (activos, ubicación anidada)
 * so the chevron matches Autocomplete popup indicators.
 */
export const findingTypePickerDropdownArrowIconSx: SxProps<Theme> = {
  fontSize: '24px',
  width: '24px',
  height: '24px',
  color: (theme: Theme) => theme.palette.action.active,
  flexShrink: 0,
  display: 'block',
};

/**
 * Applied to TextField `sx` inside Autocomplete (mandatory requirements, assignees, legacy location).
 */
export const findingTypeAutocompleteTextFieldSx: SxProps<Theme> = {
  width: '100%',
  boxSizing: 'border-box',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    minHeight: '32px',
    padding: '0 48px 0 12px !important',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    alignContent: 'center',
    '& .MuiOutlinedInput-notchedOutline': {
      border: (theme: Theme) => `1px solid ${theme.palette.secondary.light}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (theme: Theme) => theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: (theme: Theme) => `1px solid ${theme.palette.primary.main}`,
    },
  },
  '& .MuiAutocomplete-popupIndicator': popupIndicatorButtonSx,
  '& .MuiAutocomplete-clearIndicator': {
    color: (theme: Theme) => theme.palette.action.active,
    padding: 0,
  },
  '& .MuiAutocomplete-input': {
    padding: '0 !important',
    minWidth: '30px',
    lineHeight: '20px',
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-endAdornment': endAdornmentSx,
  '& .MuiAutocomplete-tag': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '4px',
    marginRight: '4px',
    maxHeight: `${findingsChipHeightPx}px`,
  },
};

/**
 * Applied to **MUI** `Autocomplete` root `sx` (Finding Type mandatory field — not ComponentLibrary wrapper).
 */
export const findingTypeMuiAutocompleteRootSx: SxProps<Theme> = {
  width: '100%',
  boxSizing: 'border-box',
  '& .MuiAutocomplete-inputRoot': {
    minHeight: '32px',
    padding: '0 48px 0 12px !important',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    boxSizing: 'border-box',
    alignItems: 'center',
  },
  '& .MuiAutocomplete-input': {
    padding: '0 !important',
    lineHeight: '20px',
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-popupIndicator': popupIndicatorButtonSx,
  '& .MuiAutocomplete-endAdornment': endAdornmentSx,
  '& .MuiAutocomplete-tag': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '4px',
    marginRight: '4px',
    maxHeight: `${findingsChipHeightPx}px`,
  },
};

/**
 * Applied to Autocomplete root `sx` (e.g. TextInputWithChips) where the input uses `MuiAutocomplete-inputRoot`.
 */
export const findingTypeAutocompleteInputRootSx: SxProps<Theme> = {
  '& .MuiAutocomplete-inputRoot': {
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    minHeight: '32px',
    padding: '0 48px 0 12px !important',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    boxSizing: 'border-box',
    alignItems: 'center',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: (theme: Theme) => theme.palette.secondary.light,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (theme: Theme) => theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 1,
      borderColor: (theme: Theme) => theme.palette.primary.main,
    },
    '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: (theme: Theme) => theme.palette.secondary.light,
    },
  },
  '& .MuiAutocomplete-popupIndicator': popupIndicatorButtonSx,
  '& .MuiAutocomplete-clearIndicator': {
    padding: 0,
  },
  '& .MuiAutocomplete-input': {
    padding: '0 !important',
    minWidth: '20ch',
    lineHeight: '20px',
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-endAdornment': endAdornmentSx,
  '& .MuiAutocomplete-tag': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '4px',
    marginRight: '4px',
    maxHeight: `${findingsChipHeightPx}px`,
  },
};

/**
 * MUI Chip applies negative margins on `.MuiChip-deleteIcon` (e.g. small size),
 * which shrinks the flex box and lets the delete control paint past the pill background.
 * Keep margins non-negative so the #F0F5FF background fully wraps label + icon.
 */
export const findingsChipSx: SxProps<Theme> = {
  borderRadius: `${findingsChipHeightPx / 2}px`,
  backgroundColor: findingsChipBackground,
  border: 'none',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  height: `${findingsChipHeightPx}px`,
  minHeight: `${findingsChipHeightPx}px`,
  maxHeight: `${findingsChipHeightPx}px`,
  paddingRight: '4px',
  /**
   * MUI Autocomplete adds `.MuiAutocomplete-tag` with default `margin: 3px` (±6px vertical).
   * Remove vertical margin so the chip height matches `findingsChipHeightPx`.
   */
  '&.MuiAutocomplete-tag': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '4px',
    marginRight: '4px',
  },
  '& .MuiChip-label': {
    paddingLeft: '10px',
    paddingRight: '4px',
    fontSize: '12px',
    lineHeight: '18px',
    color: '#3B3C40',
  },
  '& .MuiChip-deleteIcon': {
    marginTop: 0,
    marginRight: '6px',
    marginBottom: 0,
    marginLeft: '4px',
    padding: 0,
    width: '14px',
    height: '14px',
    flexShrink: 0,
  },
};
